import { MAGNET_APPEARANCE_PRESETS, magnetAppearancePreset, matchMagnetAppearancePreset } from '@sse/shared';
import '../styles/magnet-style-gallery.css';

const esc = value => String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const clone = value => JSON.parse(JSON.stringify(value));

// An explicit allowlist: no content, routes, connections or publication fields
// can enter the style patch. Every preview is derived from the same appearance
// snapshot, never the previously previewed preset.
export function stylePatch(baseline, id, mode, useColors = false) {
  const style = MAGNET_APPEARANCE_PRESETS.find(item => item.id === id);
  const preset = magnetAppearancePreset(id, mode);
  if (!preset) return null;
  const custom = !matchMagnetAppearancePreset(baseline.font, baseline.theme_vars);
  return {
    ...preset,
    ...(Object.hasOwn(baseline, 'visual_style') ? { visual_style: preset.visual_style || '' } : {}),
    theme_vars: { ...preset.theme_vars, ...(!useColors && custom ? baseline.theme_vars : {}) },
    theme_color: useColors ? style.brand : baseline.theme_color || '#0072ba',
  };
}

export function styleGalleryHTML({ visualDesignAvailable = false } = {}) {
  return `<section class="mag-style-gallery" data-style-controls aria-labelledby="mag-style-label">
    <div class="mag-gallery-head"><div><h3 id="mag-style-label">Find your style</h3><p>Try a look with your own conversation. Apply only when it feels right.</p></div>
    <button type="button" class="mag-gallery-link" data-style-all aria-expanded="false">See all</button></div>
    <div class="mag-gallery-tools"><div role="group" aria-label="Style preview theme"><button type="button" data-style-mode="light" aria-pressed="true">Light</button><button type="button" data-style-mode="dark" aria-pressed="false">Dark</button></div>
    <div><button type="button" data-style-prev aria-label="Previous styles">←</button><button type="button" data-style-next aria-label="Next styles">→</button></div></div>
    <div class="mag-style-grid" role="group" aria-label="Appearance styles, scroll for more" tabindex="0">
      ${MAGNET_APPEARANCE_PRESETS.filter(p => visualDesignAvailable || !p.visual_style).map(p => `<button type="button" class="mag-style-card" data-appearance-preset="${p.id}" aria-pressed="false" aria-label="Preview ${p.name}" style="--mag-style-font:${esc(p.font)}">
      <span class="mag-style-sample" aria-hidden="true"><span class="mag-style-minihead"><span class="mag-style-mark">M</span><b>Your brand</b></span><span class="mag-style-question">How can we help?</span><span class="mag-style-answer">Let’s get started</span><span class="mag-style-option">Explore my options <span>→</span></span></span>
      <strong>${p.name}</strong><small>${p.description}</small></button>`).join('')}
    </div>
    <div class="mag-gallery-pending" data-style-pending hidden>
      <label><input type="checkbox" data-style-colors> Use preset colors</label>
      <p class="mag-modal__hint">Your brand color and custom palette stay in place unless you choose this.</p>
      <div data-style-remember-row hidden><label><input type="checkbox" data-style-remember aria-describedby="mag-style-remember-help"> Use as my starting style for new magnets</label><p class="mag-modal__hint" id="mag-style-remember-help">Remembered in this browser for this workspace. You can choose a different style anytime.</p></div>
      <div class="mag-gallery-actions"><button type="button" class="mag-modal__btn mag-modal__btn--primary" data-style-apply>Apply style</button><button type="button" class="mag-modal__btn mag-modal__btn--ghost" data-style-cancel>Keep my design</button></div>
    </div>
    <p class="mag-modal__hint" id="mag-style-status" role="status"></p>
    <button type="button" class="mag-gallery-link" data-style-undo hidden>Undo applied style</button>
  </section>`;
}

export function mountStyleGallery(root, { read, write, onApply, onPreview, remember }) {
  const gallery = root.querySelector('.mag-style-gallery');
  const grid = gallery.querySelector('.mag-style-grid');
  const status = gallery.querySelector('#mag-style-status');
  const colors = gallery.querySelector('[data-style-colors]');
  gallery.querySelector('[data-style-remember-row]').hidden = !remember;
  const cards = [...grid.querySelectorAll('[data-appearance-preset]')];
  let pending = null, baseline = null, undo = null;
  let mode = read().theme_mode === 'dark' ? 'dark' : 'light';
  const patch = () => pending ? stylePatch(baseline, pending, mode, colors.checked) : null;
  const sync = () => {
    const current = read();
    const selected = pending || matchMagnetAppearancePreset(current.font, current.theme_vars, current.visual_style);
    // New-draft runtime eligibility is determined after creation. Until that
    // flow supports design capabilities, don't offer a preference it may reject.
    gallery.querySelector('[data-style-remember-row]').hidden = !remember || !!MAGNET_APPEARANCE_PRESETS.find(p => p.id === pending)?.visual_style;
    for (const card of cards) {
      const preset = MAGNET_APPEARANCE_PRESETS.find(p => p.id === card.dataset.appearancePreset);
      const palette = preset[mode];
      card.setAttribute('aria-pressed', String(preset.id === selected));
      for (const [key, value] of Object.entries(palette)) card.style.setProperty(`--mag-style-${key}`, value);
      card.style.setProperty('--mag-style-brand', colors.checked ? preset.brand : current.theme_color || '#0072ba');
      // Readable miniature answer text, independent of arbitrary brand colors.
      const hex = (colors.checked ? preset.brand : current.theme_color || '#0072ba').slice(1);
      const luminance = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16) / 255).map(v => v <= .04045 ? v / 12.92 : ((v + .055) / 1.055) ** 2.4).reduce((sum, v, i) => sum + v * [.2126, .7152, .0722][i], 0);
      card.style.setProperty('--mag-style-ink', luminance > .179 ? '#111111' : '#ffffff');
    }
    gallery.querySelectorAll('[data-style-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.styleMode === mode)));
    gallery.querySelector('[data-style-pending]').hidden = !pending;
    gallery.querySelector('[data-style-undo]').hidden = !undo || !!pending;
    const name = MAGNET_APPEARANCE_PRESETS.find(p => p.id === selected)?.name;
    status.textContent = pending ? `Previewing ${name} · ${mode === 'dark' ? 'Dark' : 'Light'}. Your draft is unchanged until you apply.`
      : name ? `${name} · ${current.theme_mode === 'dark' ? 'Dark' : 'Light'} applied to your draft.`
        : Object.keys(current.theme_vars || {}).length ? 'Custom palette. Your current design is preserved.' : 'Your current design. Choose a style to preview it.';
  };
  const stop = () => { pending = null; baseline = null; colors.checked = false; sync(); onPreview(); };
  cards.forEach(card => card.addEventListener('click', () => {
    if (!baseline) baseline = clone(read());
    pending = card.dataset.appearancePreset; sync(); onPreview();
  }));
  gallery.querySelectorAll('[data-style-mode]').forEach(button => button.addEventListener('click', () => { mode = button.dataset.styleMode; sync(); if (pending) onPreview(); }));
  colors.addEventListener('change', () => { sync(); onPreview(); });
  gallery.querySelector('[data-style-cancel]').addEventListener('click', stop);
  gallery.querySelector('[data-style-apply]').addEventListener('click', () => {
    if (!pending) return;
    const rememberRequested = remember && !gallery.querySelector('[data-style-remember-row]').hidden && gallery.querySelector('[data-style-remember]').checked;
    const remembered = rememberRequested ? remember({ id: pending, mode }) : null;
    gallery.querySelector('[data-style-remember]').checked = false;
    undo = clone(read()); write(patch()); pending = null; baseline = null; colors.checked = false;
    sync(); if (rememberRequested) status.textContent += remembered ? ' This style will be offered when you create a new magnet.' : ' Your browser could not remember this style.'; onApply(); gallery.querySelector('[data-style-undo]').focus({ preventScroll: true });
  });
  gallery.querySelector('[data-style-undo]').addEventListener('click', () => {
    if (!undo) return;
    write(undo); undo = null; sync(); onApply(); cards[0].focus({ preventScroll: true });
  });
  const scroll = direction => grid.scrollBy?.({ left: direction * Math.max(210, grid.clientWidth * .8), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  gallery.querySelector('[data-style-prev]').addEventListener('click', () => scroll(-1));
  gallery.querySelector('[data-style-next]').addEventListener('click', () => scroll(1));
  grid.addEventListener('keydown', event => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault(); const index = cards.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? cards.length - 1 : Math.max(0, Math.min(cards.length - 1, index + (event.key === 'ArrowRight' ? 1 : -1)));
    cards[next].focus();
  });
  gallery.querySelector('[data-style-all]').addEventListener('click', event => {
    const expanded = grid.classList.toggle('is-expanded');
    event.currentTarget.setAttribute('aria-expanded', String(expanded)); event.currentTarget.textContent = expanded ? 'Show carousel' : 'See all';
    gallery.querySelector('[data-style-prev]').hidden = expanded; gallery.querySelector('[data-style-next]').hidden = expanded;
  });
  // Editing actual design controls cancels only the temporary overlay. Nothing
  // needs restoring: the working fields were never touched by browsing.
  const edited = event => { if (!event.target.closest('[data-style-controls]')) { if (pending) stop(); else sync(); } };
  root.querySelector('.mag-modal__main').addEventListener('input', edited);
  root.querySelector('.mag-modal__main').addEventListener('change', edited);
  sync();
  return {
    sync,
    overlay(pkg) {
      const next = patch();
      if (!next) return pkg;
      const preview = { ...pkg, ...Object.fromEntries(Object.entries(next).map(([key, value]) => [`mag_${key}`, value])) };
      if (!pkg.mag_macro_steps?.length && !Object.values(pkg.mag_routes || {}).some(steps => steps?.length) && !pkg.mag_home && !pkg.mag_actions?.length) {
        preview.mag_macro_steps = [{ id: 'style-sample', step_type: 'single_select', step_prompts: ['How can we help?'], step_columns: ['preview_choice'], step_options: [{ label: 'Explore my options' }, { label: 'Talk to the team' }] }];
        status.textContent = status.textContent.replace(/ Sample conversation.*$/, '') + ' Sample conversation shown because no flow is defined yet. It will not be saved.';
      }
      return preview;
    },
    cancel: stop,
    isPending: () => !!pending,
  };
}
